import {
  CircleStackIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps } from 'react';
import { TechnologyDistributionHosting } from '../../services/technologyApi';
import { TechnologyDistributionResourceMeter } from './TechnologyDistributionResourceMeter';
import {
  UtilizationBand,
  VisibleAsset,
  bandLabel,
  chipSubtitle,
  countLabel,
  environmentSummary,
  infrastructureKindLabel,
  paletteIndex,
  singleEnvironmentKind,
  worstUtilization,
} from './technologyDistributionView';

const APPLICATION_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [
  Cog6ToothIcon,
  ComputerDesktopIcon,
  Square3Stack3DIcon,
  CircleStackIcon,
  ServerStackIcon,
  CommandLineIcon,
];

const CHIP_CLASS = [
  'bg-sky-50 text-sky-950',
  'bg-emerald-50 text-emerald-950',
  'bg-teal-50 text-teal-950',
  'bg-violet-50 text-violet-950',
  'bg-cyan-50 text-cyan-950',
  'bg-rose-50 text-rose-950',
  'bg-amber-50 text-amber-950',
  'bg-indigo-50 text-indigo-950',
];

const ENVIRONMENT_BADGE: Record<string, string> = {
  Production: 'bg-emerald-50 text-emerald-700',
  Homologation: 'bg-sky-50 text-sky-700',
  Development: 'bg-amber-50 text-amber-800',
  DisasterRecovery: 'bg-violet-50 text-violet-700',
};

const STATUS_BADGE: Record<UtilizationBand, string> = {
  unknown: 'bg-secondary-100 text-secondary-600',
  low: 'bg-emerald-50 text-emerald-700',
  moderate: 'bg-amber-50 text-amber-800',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-rose-50 text-rose-700',
};

type TechnologyDistributionServerCardProps = {
  item: VisibleAsset;
  selected: boolean;
  onSelect: () => void;
};

function cardBorder(selected: boolean, band: UtilizationBand): string {
  if (selected) return 'border-primary-400 ring-2 ring-primary-100';
  if (band === 'critical') return 'border-rose-300';
  if (band === 'high') return 'border-orange-200';
  return 'border-secondary-200 hover:border-primary-200';
}

function hostingChipClass(applicationId: string): string {
  return CHIP_CLASS[paletteIndex(applicationId, CHIP_CLASS.length)];
}

export function HostingChip({
  hosting,
  siblings,
}: {
  hosting: TechnologyDistributionHosting;
  siblings: readonly TechnologyDistributionHosting[];
}) {
  const Icon = APPLICATION_ICONS[paletteIndex(hosting.applicationId, APPLICATION_ICONS.length)];
  return (
    <span className={`block min-w-0 rounded-xl px-2.5 py-2 ${hostingChipClass(hosting.applicationId)}`}>
      <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
        <span className="truncate">{hosting.applicationName}</span>
      </span>
      <span className="mt-0.5 block truncate text-xs opacity-70">{chipSubtitle(hosting, siblings)}</span>
    </span>
  );
}

export function TechnologyDistributionServerCard({
  item,
  selected,
  onSelect,
}: TechnologyDistributionServerCardProps) {
  const band = worstUtilization(item.asset);
  const applicationCount = new Set(item.hostings.map((hosting) => hosting.applicationId)).size;
  const environment = environmentSummary(item.hostings);
  const environmentKind = singleEnvironmentKind(item.hostings);
  const meta = [infrastructureKindLabel(item.asset.kind), item.asset.vendor, item.asset.ipAddress]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex h-full w-full flex-col rounded-2xl border bg-white p-4 text-left shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${cardBorder(selected, band)}`}
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-base font-semibold text-secondary-900">{item.asset.name}</span>
            {environment ? (
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  environmentKind
                    ? ENVIRONMENT_BADGE[environmentKind] ?? 'bg-secondary-100 text-secondary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}
              >
                {environment}
              </span>
            ) : null}
          </span>
          {meta ? <span className="mt-1 block truncate text-xs text-secondary-500">{meta}</span> : null}
          <span className="mt-2 block text-sm text-secondary-600">
            {countLabel(applicationCount, 'aplicação', 'aplicações')}
          </span>
        </span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[band]}`}>
          {bandLabel(band)}
        </span>
      </span>

      <span className="mt-4 block">
        <TechnologyDistributionResourceMeter asset={item.asset} variant="card" />
      </span>

      {item.hostings.length === 0 ? (
        <span className="mt-4 block text-sm text-secondary-400">Nenhuma aplicação hospedada.</span>
      ) : (
        <span className="mt-4 grid grid-cols-2 gap-2">
          {item.hostings.map((hosting) => (
            <HostingChip key={hosting.id} hosting={hosting} siblings={item.hostings} />
          ))}
        </span>
      )}
    </button>
  );
}
